import { useState, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Utensils, Cake, MapPin, ShoppingCart, CheckCircle, Loader,
  Plus, Minus, Trash2, Calendar, User, Phone, CreditCard,
  Banknote, ArrowLeft, ArrowRight, X, UtensilsCrossed, Mail,
  Beef, Croissant, Sandwich, Cookie, Coffee, ChefHat, Search, Star,
  CakeSlice, ExternalLink, MessageSquare, Copy, ClipboardCheck,
  Pencil, Check, Eye, EyeOff, Clock,
} from 'lucide-react'
import { BUSINESSES as FALLBACK_BUSINESSES } from '../../data/orderMenu'
import { formatDate } from '../../lib/calculations'
import { getOrderingPermissions } from '../../lib/publicData'
import { getOrSeedCustomerMenu, SEED_BUSINESSES } from '../../lib/customerMenuService'
import { getAllBusinessThumbnails, getFullImages } from '../../lib/imageService'
import { getPriceOverrides, savePriceOverride, getAvailability, setItemAvailability } from '../../lib/priceService'
import { useAuth } from '../../context/AuthContext'
import ImageSlideshow from '../../components/common/ImageSlideshow'

// TODO: Replace with real Google Business Profile review URLs
const GOOGLE_REVIEW_URL_LIMPOPO = 'https://search.google.com/local/writereview?placeid=PLACEHOLDER_LIMPOPO'
const GOOGLE_REVIEW_URL_PRETORIA = 'https://g.page/r/CQypvJPLtxWvEAI/review'

const SUGGESTED_REVIEWS = {
  5: 'Amazing food and great service — highly recommend Desres Kitchen!',
  4: 'Really enjoyed the food at Desres Kitchen. Will definitely be back!',
  3: 'Good food at Desres Kitchen. A solid experience overall.',
  2: 'Decent experience at Desres Kitchen. Some things could be improved.',
  1: 'Had an experience at Desres Kitchen worth sharing on Google.',
}

const CATEGORY_ICONS = {
  meals: Beef,
  bakes: Croissant,
  sandwiches: Sandwich,
  sweets: Cookie,
  'custom-cake': CakeSlice,
  'daily-bread': Croissant,
  'rolls-buns': Croissant,
  'fast-food': Utensils,
  'quick-bakes': Cookie,
  'buttered-bread': Sandwich,
  'coffee-tea': Coffee,
  extras: UtensilsCrossed,
  schoolbuns: Cake,
  kota: Sandwich,
  'bento-cakes': CakeSlice,
}

function getMinDate() {
  const d = new Date()
  d.setTime(d.getTime() + 12 * 60 * 60 * 1000)
  return d.toISOString().split('T')[0]
}

const BIZ_ICONS = { desres: Utensils, yawehs: Cake }

export default function OrderPage() {
  const { user } = useAuth() || {}
  const [step, setStep] = useState('business')
  const [business, setBusiness] = useState(null)
  const [branch, setBranch] = useState(null)
  const [cart, setCart] = useState([])
  const [activeCategory, setActiveCategory] = useState(null) // null = "All"
  const [menuSearch, setMenuSearch] = useState('')
  const [viewAllCat, setViewAllCat] = useState(null)
  const [priceOverrides, setPriceOverrides] = useState({})
  const [editingPrice, setEditingPrice] = useState(null) // item id being edited
  const [editPriceValue, setEditPriceValue] = useState('')
  const [availability, setAvailability] = useState({}) // { itemId: false } for disabled
  const [form, setForm] = useState({ name: '', whatsapp: '', email: '', collectionDate: '' })
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [orderRef, setOrderRef] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [orderingPermissions, setOrderingPermissions] = useState(null)
  const [BUSINESSES, setBUSINESSES] = useState(SEED_BUSINESSES)
  const [detailItem, setDetailItem] = useState(null) // product detail modal
  const [detailNote, setDetailNote] = useState('')
  const [detailQty, setDetailQty] = useState(1)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewRating, setReviewRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [reviewName, setReviewName] = useState('')
  const [reviewBranch, setReviewBranch] = useState('limpopo')
  const [reviewSubmitting, setReviewSubmitting] = useState(false)
  const [reviewSubmitted, setReviewSubmitted] = useState(false)
  const [reviewHover, setReviewHover] = useState(0)
  const [copied, setCopied] = useState(false)
  const [productThumbs, setProductThumbs] = useState(new Map())
  const [detailFullImages, setDetailFullImages] = useState(null)
  const chipsRef = useRef(null)
  const navigate = useNavigate()

  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0)
  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0)

  const addToCart = (item, qty = 1, note = '') => {
    const price = getPrice(item)
    setCart(prev => {
      const existing = prev.find(c => c.id === item.id)
      if (existing) {
        return prev.map(c => c.id === item.id
          ? { ...c, price, quantity: c.quantity + qty, ...(note ? { note } : {}) }
          : c
        )
      }
      return [...prev, { id: item.id, name: item.name, price, quantity: qty, note: note || '' }]
    })
  }

  const updateQty = (id, delta) => {
    setCart(prev => prev.map(c => {
      if (c.id !== id) return c
      const newQty = c.quantity + delta
      return newQty > 0 ? { ...c, quantity: newQty } : c
    }).filter(c => c.quantity > 0))
  }

  const removeFromCart = (id) => setCart(prev => prev.filter(c => c.id !== id))
  const getItemQty = (id) => cart.find(c => c.id === id)?.quantity || 0
  const getItemNote = (id) => cart.find(c => c.id === id)?.note || ''

  const openDetail = (item) => {
    const qty = getItemQty(item.id)
    setDetailItem(item)
    setDetailQty(qty || 1)
    setDetailNote(getItemNote(item.id))
    // Load full-res images on demand
    setDetailFullImages(null)
    if (business) {
      getFullImages(business.key, item.id).then(setDetailFullImages).catch(() => setDetailFullImages([]))
    }
  }

  const closeDetail = () => {
    setDetailItem(null)
    setDetailNote('')
    setDetailQty(1)
    setDetailFullImages(null)
  }

  const confirmDetail = () => {
    if (!detailItem) return
    const price = getPrice(detailItem)
    const existingQty = getItemQty(detailItem.id)
    if (existingQty > 0) {
      // Update existing cart item
      setCart(prev => prev.map(c =>
        c.id === detailItem.id
          ? { ...c, price, quantity: detailQty, note: detailNote }
          : c
      ).filter(c => c.quantity > 0))
    } else {
      addToCart(detailItem, detailQty, detailNote)
    }
    closeDetail()
  }

  useEffect(() => {
    getOrderingPermissions().then(p => {
      const { updatedAt, ...perms } = p
      setOrderingPermissions(perms)
    }).catch(() => setOrderingPermissions({}))
    // Load menu from Firestore (auto-seeds if empty)
    getOrSeedCustomerMenu().then(biz => {
      if (biz && biz.length > 0) setBUSINESSES(biz)
    }).catch(() => setBUSINESSES(SEED_BUSINESSES)) // always fall back to correct seed data
  }, [])

  useEffect(() => { window.scrollTo(0, 0) }, [step])
  useEffect(() => { setActiveCategory(null) }, [business])

  // Fetch thumbnails, price overrides, and availability when a business is selected
  useEffect(() => {
    if (!business) return
    // Graceful degradation — empty UI falls back cleanly if any of
    // these fail. Sentry still captures so broken public-site data
    // reaches visibility.
    getAllBusinessThumbnails(business.key).then(setProductThumbs).catch(err => {
      Sentry.captureException(err, { extra: { fn: 'Order.getAllBusinessThumbnails', businessKey: business.key } })
    })
    getPriceOverrides(business.key).then(setPriceOverrides).catch(err => {
      Sentry.captureException(err, { extra: { fn: 'Order.getPriceOverrides', businessKey: business.key } })
    })
    getAvailability(business.key).then(setAvailability).catch(err => {
      Sentry.captureException(err, { extra: { fn: 'Order.getAvailability', businessKey: business.key } })
    })
  }, [business])

  // Get effective price for an item (override or default)
  const getPrice = (item) => {
    const override = priceOverrides[item.id]
    return override !== undefined ? override : item.price
  }

  // Check if item is available (default = true)
  const isAvailable = (itemId) => availability[itemId] !== false

  // Toggle availability
  const toggleAvailability = async (itemId) => {
    const newVal = !isAvailable(itemId)
    setAvailability(prev => ({ ...prev, [itemId]: newVal }))
    try {
      await setItemAvailability(business.key, itemId, newVal)
    } catch (err) {
      console.error('Availability toggle failed:', err)
      setAvailability(prev => ({ ...prev, [itemId]: !newVal }))
    }
  }

  // Save a price edit
  const handlePriceSave = async (itemId) => {
    const val = parseFloat(editPriceValue)
    if (isNaN(val) || val < 0) return
    try {
      await savePriceOverride(business.key, itemId, val)
      setPriceOverrides(prev => ({ ...prev, [itemId]: val }))
      setEditingPrice(null)
      setEditPriceValue('')
    } catch (err) {
      console.error('Price save failed:', err)
    }
  }

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const paymentResult = params.get('payment')
    const refId = params.get('order_id') || params.get('ref')
    if (paymentResult === 'success') {
      // Redirect to the dedicated success page
      const successParams = new URLSearchParams()
      if (refId) successParams.set('ref', refId)
      successParams.set('payment', 'Paid via Card')
      window.location.replace(`/order/success?${successParams.toString()}`)
    } else if (paymentResult === 'cancelled' || paymentResult === 'failed') {
      setError(paymentResult === 'cancelled' ? 'Payment was cancelled.' : 'Payment failed. Please try again or choose cash.')
      setStep('payment')
      window.history.replaceState({}, '', '/')
    }
  }, [])

  const goToSuccess = (ref, paymentLabel) => {
    const params = new URLSearchParams()
    if (ref) params.set('ref', ref)
    if (form.name) params.set('name', form.name.trim())
    if (business) params.set('business', business.label)
    if (branch) params.set('branch', branch.label)
    if (form.collectionDate) params.set('date', formatDate(form.collectionDate))
    if (cartTotal) params.set('total', cartTotal.toFixed(2))
    if (paymentLabel) params.set('payment', paymentLabel)
    navigate(`/order/success?${params.toString()}`)
  }

  const [reviewError, setReviewError] = useState('')
  const handleReviewSubmit = async () => {
    if (!reviewRating) return
    setReviewSubmitting(true)
    setReviewError('')
    try {
      const res = await fetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_id: reviewBranch,
          product_name: reviewBranch === 'pretoria' ? "Yahweh's Delight" : 'Desres Kitchen',
          business: reviewBranch === 'pretoria' ? "Yahweh's Delight" : 'Desres Kitchen',
          category: 'general',
          rating: reviewRating,
          comment: reviewText.trim(),
          reviewer_name: reviewName.trim() || 'Anonymous',
        }),
      })
      if (!res.ok) throw new Error('Failed to submit review')
      setReviewSubmitted(true)
    } catch (err) {
      console.error('Review submit error:', err)
      setReviewError('Something went wrong. Please try again.')
    }
    setReviewSubmitting(false)
  }

  const reviewTextForCopy = reviewText.trim() || SUGGESTED_REVIEWS[reviewRating] || ''

  const handleCopyReview = async () => {
    try {
      await navigator.clipboard.writeText(reviewTextForCopy)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback: select the text
      const el = document.getElementById('review-copy-text')
      if (el) {
        const range = document.createRange()
        range.selectNodeContents(el)
        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
      }
    }
  }

  const resetReview = () => {
    setReviewRating(0)
    setReviewText('')
    setReviewName('')
    setReviewBranch('limpopo')
    setReviewSubmitted(false)
    setCopied(false)
  }

  const handleCashOrder = async () => {
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          whatsapp: form.whatsapp.trim(),
          email: form.email.trim(),
          items: cart.map(i => ({ description: i.name, quantity: i.quantity, price: i.price, ...(i.note ? { note: i.note } : {}) })),
          collection_date: form.collectionDate,
          business: business.label,
          branch: branch.address,
          total_amount: cartTotal,
          payment_method: 'cash',
          payment_status: 'awaiting_payment',
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to submit order')
      goToSuccess(data.order_id || '', 'Cash - Pay on Collection')
    } catch (err) {
      setError(err.message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleCardPayment = async () => {
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/payments/yoco', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: cartTotal,
          name: form.name.trim(),
          whatsapp: form.whatsapp.trim(),
          email: form.email.trim(),
          items: cart.map(i => ({ description: i.name, quantity: i.quantity, price: i.price, ...(i.note ? { note: i.note } : {}) })),
          collection_date: form.collectionDate,
          business: business.label,
          branch: branch.address,
        }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to initiate payment')
      const url = data.payment_url || data.redirect_url
      if (url) window.location.href = url
      else throw new Error('No payment URL received')
    } catch (err) {
      setError(err.message)
      setSubmitting(false)
    }
  }

  // ─── HEADER ──────────────────────────────────────────
  const Header = ({ title, subtitle, label = 'Order' }) => (
    <section className="bg-charcoal relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-6 h-0.5 bg-terracotta" />
          <span className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta">{label}</span>
        </div>
        <h1 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(36px, 5vw, 72px)' }}>
          {title}
        </h1>
        {subtitle && <p className="text-sm text-muted mt-3 max-w-xl">{subtitle}</p>}
      </div>
    </section>
  )

  const BackBtn = ({ onClick, label = 'Back' }) => (
    <button onClick={onClick} className="text-sm text-terracotta hover:underline mb-6 inline-flex items-center gap-1 font-body tracking-[0.05em] uppercase">
      <ArrowLeft size={14} /> {label}
    </button>
  )

  // ─── STEP 1: SELECT LOCATION (business + branch combined) ─────────────────────────
  // Check if a business has ANY enabled categories (if all are off, hide it)
  const isBusinessOpen = (biz) => {
    if (!orderingPermissions) return true // still loading — show all
    return biz.categories.some(cat =>
      orderingPermissions[`${biz.key}__${cat.key}`] !== false
    )
  }

  const LOCATIONS = BUSINESSES
    .filter(biz => biz.customerFacing !== false && isBusinessOpen(biz))
    .map(biz => ({
      biz,
      branch: biz.key === 'yawehs'
        ? (biz.branches.find(b => b.key === 'pretoria') || biz.branches[0])
        : biz.branches[0],
      city: biz.key === 'desres' ? 'Limpopo' : 'Pretoria',
    }))

  const TESTIMONIALS = [
    { name: 'Thandi M.', initials: 'TM', label: 'Regular Customer', rating: 5, imageUrl: null, text: 'The food is always fresh and full of flavour. My family orders every week!' },
    { name: 'Sipho K.', initials: 'SK', label: 'Verified Buyer', rating: 5, imageUrl: null, text: 'Best value meals in Limpopo. The portions are generous and the taste is unmatched.' },
    { name: 'Refilwe D.', initials: 'RD', label: 'Local Guide', rating: 4, imageUrl: null, text: 'Ordered for a work event and everyone was impressed. Will definitely order again!' },
  ]

  const heroImageUrl = null

  if (step === 'business' || step === 'branch') {
    return (
      <div>
        {/* ─── HERO SECTION ─────────────────────────────── */}
        <section className="bg-umber">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-16 pb-0">
            <div className="relative overflow-hidden" style={{ background: '#1C1C1C', borderRadius: '20px', padding: 'clamp(40px, 6vw, 60px)' }}>
              {heroImageUrl ? (
                <img src={heroImageUrl} alt="Desres Kitchen food" className="absolute inset-0 w-full h-full object-cover" style={{ borderRadius: '20px' }} />
              ) : (
                <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at 80% 50%, #2C1A0E 0%, #1C1C1C 70%)' }} />
              )}
              {heroImageUrl && <div className="absolute inset-0 bg-black/60" style={{ borderRadius: '20px' }} />}
              <div className="relative z-10 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
                <div className="flex-1 text-center lg:text-left">
                  <span className="inline-block font-body text-[12px] font-semibold tracking-[0.1em] uppercase mb-4" style={{ color: '#E8761A' }}>Since 1998</span>
                  <h1 className="font-display tracking-[0.04em] leading-[0.95] mb-4" style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, color: '#FFFFFF' }}>
                    Desres Kitchen<br />&amp; Confectionery
                  </h1>
                  <p className="mb-8 max-w-lg mx-auto lg:mx-0" style={{ color: '#B0A89A', fontSize: '16px' }}>
                    Authentic South African cuisine, freshly baked daily. From traditional Mukokoroshi platters to custom cakes — made with love for over 25 years.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                    <button
                      onClick={() => document.getElementById('location-select')?.scrollIntoView({ behavior: 'smooth' })}
                      className="rounded-full bg-[#E8761A] hover:bg-[#CF6210] px-8 py-3.5 font-body text-[13px] font-bold tracking-[0.1em] uppercase transition-all hover:-translate-y-0.5"
                      style={{ color: '#FFFFFF' }}
                    >
                      Order Now
                    </button>
                    <Link
                      to="/menu"
                      className="rounded-full bg-transparent border border-white/30 px-8 py-3.5 font-body text-[13px] font-bold tracking-[0.1em] uppercase hover:border-white hover:bg-white/5 transition-all text-center"
                      style={{ color: '#FFFFFF' }}
                    >
                      View Menu
                    </Link>
                    <button
                      onClick={() => { resetReview(); setShowReviewModal(true) }}
                      className="rounded-full bg-transparent border border-white/30 px-8 py-3.5 font-body text-[13px] font-bold tracking-[0.1em] uppercase hover:border-[#C9A027] hover:bg-white/5 transition-all"
                      style={{ color: '#FFFFFF' }}
                    >
                      <span className="inline-flex items-center gap-2"><Star size={16} /> Leave a Review</span>
                    </button>
                  </div>
                </div>
                <div className="hidden lg:flex flex-1 items-center justify-center">
                  <div className="w-72 h-72 rounded-2xl overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #2C1A0E, #1C1C1C)', aspectRatio: '1' }}>
                    <img src="/logo.jpg" alt="Desres Kitchen" className="w-48 h-48 object-contain" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* ─── REVIEW MODAL ─────────────────────────────── */}
        {showReviewModal && (
          <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center" onClick={() => setShowReviewModal(false)}>
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div
              className="relative w-full sm:max-w-lg max-h-[90vh] overflow-y-auto bg-umber border-t sm:border border-border-dark"
              onClick={e => e.stopPropagation()}
            >
              {/* Close button */}
              <button
                onClick={() => setShowReviewModal(false)}
                className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-muted hover:text-cream transition-colors"
              >
                <X size={20} />
              </button>

              {!reviewSubmitted ? (
                <div className="p-6 sm:p-8">
                  <div className="text-center mb-8">
                    <span className="inline-block font-body text-[11px] font-semibold tracking-[0.3em] uppercase text-terracotta mb-3">Your Feedback</span>
                    <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(28px, 4vw, 42px)' }}>
                      How Was Your Experience?
                    </h2>
                    <p className="text-sm text-muted mt-3">We'd love to hear from you.</p>
                  </div>

                  <div className="space-y-6">
                    {/* Star Rating */}
                    <div className="text-center">
                      <label className="text-xs text-muted font-body uppercase tracking-wider block mb-3">Tap to rate *</label>
                      <div className="flex justify-center gap-2">
                        {[1, 2, 3, 4, 5].map(star => (
                          <button
                            key={star}
                            type="button"
                            onClick={() => setReviewRating(star)}
                            onMouseEnter={() => setReviewHover(star)}
                            onMouseLeave={() => setReviewHover(0)}
                            className="transition-transform hover:scale-110 p-1"
                            style={{ minWidth: '44px', minHeight: '44px' }}
                          >
                            <Star
                              size={32}
                              className={`transition-colors ${
                                star <= (reviewHover || reviewRating)
                                  ? 'text-ochre fill-ochre'
                                  : 'text-border-darker'
                              }`}
                            />
                          </button>
                        ))}
                      </div>
                      {reviewRating > 0 && (
                        <p className="text-sm text-ochre mt-2 font-body font-semibold">
                          {reviewRating === 1 ? 'Poor' : reviewRating === 2 ? 'Fair' : reviewRating === 3 ? 'Good' : reviewRating === 4 ? 'Very Good' : 'Excellent'}
                        </p>
                      )}
                    </div>

                    {/* Branch Selector */}
                    <div>
                      <label className="text-xs text-muted font-body uppercase tracking-wider block mb-3">Which branch did you visit?</label>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setReviewBranch('limpopo')}
                          className={`flex items-center gap-3 p-4 border transition-all ${
                            reviewBranch === 'limpopo' ? 'border-terracotta bg-ember-bg' : 'border-border-dark hover:border-terracotta/40'
                          }`}
                        >
                          <MapPin size={18} className={reviewBranch === 'limpopo' ? 'text-terracotta' : 'text-muted'} />
                          <div className="text-left">
                            <span className={`text-sm font-body font-semibold block ${reviewBranch === 'limpopo' ? 'text-terracotta' : 'text-cream'}`}>Limpopo</span>
                            <span className="text-[11px] text-muted">Desres Kitchen</span>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setReviewBranch('pretoria')}
                          className={`flex items-center gap-3 p-4 border transition-all ${
                            reviewBranch === 'pretoria' ? 'border-terracotta bg-ember-bg' : 'border-border-dark hover:border-terracotta/40'
                          }`}
                        >
                          <MapPin size={18} className={reviewBranch === 'pretoria' ? 'text-terracotta' : 'text-muted'} />
                          <div className="text-left">
                            <span className={`text-sm font-body font-semibold block ${reviewBranch === 'pretoria' ? 'text-terracotta' : 'text-cream'}`}>Pretoria</span>
                            <span className="text-[11px] text-muted">Yahweh's Delight</span>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Written Review */}
                    <div>
                      <label className="text-xs text-muted font-body uppercase tracking-wider block mb-2">
                        Tell us about your experience <span className="normal-case tracking-normal">(optional)</span>
                      </label>
                      <textarea
                        value={reviewText}
                        onChange={e => setReviewText(e.target.value.slice(0, 500))}
                        placeholder="What did you enjoy? What could we do better?"
                        rows={3}
                        maxLength={500}
                        className="w-full bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta resize-none"
                      />
                      <p className="text-[10px] text-muted text-right mt-1">{reviewText.length}/500</p>
                    </div>

                    {/* Name */}
                    <div>
                      <label className="text-xs text-muted font-body uppercase tracking-wider block mb-2">
                        Your name <span className="normal-case tracking-normal">(optional)</span>
                      </label>
                      <input
                        type="text"
                        value={reviewName}
                        onChange={e => setReviewName(e.target.value)}
                        placeholder="E.g. Thandi"
                        className="w-full bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta"
                      />
                    </div>

                    {reviewError && (
                      <p className="text-sm text-terracotta font-body text-center">{reviewError}</p>
                    )}

                    {/* Submit */}
                    <button
                      onClick={handleReviewSubmit}
                      disabled={!reviewRating || reviewSubmitting}
                      className="w-full bg-terracotta hover:bg-terracotta-glow disabled:opacity-50 text-white py-4 font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
                    >
                      {reviewSubmitting ? (
                        <><Loader size={16} className="animate-spin" /> Submitting...</>
                      ) : (
                        <><Star size={16} /> Submit Review</>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="p-6 sm:p-8">
                  {/* Thank you */}
                  <div className="text-center mb-8">
                    <CheckCircle size={48} className="text-terracotta mx-auto mb-4" />
                    <h2 className="font-display text-cream text-2xl tracking-wide mb-3">
                      Thank you for your review!
                    </h2>
                    <p className="text-sm text-ink-mid leading-relaxed max-w-md mx-auto">
                      Your review means the world to us. Want to share it on Google too? We've already saved your words for you.
                    </p>
                  </div>

                  {/* Their review text */}
                  <div className="mb-5">
                    <label className="text-xs text-muted font-body uppercase tracking-wider block mb-2">Your review (ready to paste):</label>
                    <div
                      id="review-copy-text"
                      className="bg-surface-high border border-border-dark p-4 font-body text-sm text-cream leading-relaxed"
                      style={{ userSelect: 'text' }}
                    >
                      {reviewTextForCopy}
                    </div>
                  </div>

                  {/* Copy button */}
                  <button
                    onClick={handleCopyReview}
                    className={`w-full flex items-center justify-center gap-2 py-3 font-body text-[13px] font-bold tracking-[0.1em] uppercase border transition-all mb-4 ${
                      copied
                        ? 'bg-surface-high border-terracotta text-terracotta'
                        : 'bg-surface border-border-dark text-cream hover:border-terracotta hover:text-terracotta'
                    }`}
                  >
                    {copied ? (
                      <><ClipboardCheck size={16} /> Copied!</>
                    ) : (
                      <><Copy size={16} /> Copy my review</>
                    )}
                  </button>

                  {/* Open Google Review */}
                  <a
                    href={reviewBranch === 'pretoria' ? GOOGLE_REVIEW_URL_PRETORIA : GOOGLE_REVIEW_URL_LIMPOPO}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full bg-terracotta hover:bg-terracotta-glow text-white py-4 font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 mb-3"
                  >
                    <ExternalLink size={16} /> Leave this review on Google
                  </a>
                  <p className="text-xs text-muted text-center mb-6">
                    Google will open in a new tab. Just paste your review and tap post — done!
                  </p>

                  <p className="text-[11px] text-muted text-center border-t border-border-dark pt-5">
                    On Google: tap the stars, paste your review in the text box, then tap Post.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
                    <button
                      onClick={resetReview}
                      className="bg-surface border border-border-dark text-cream px-6 py-3 font-body text-[12px] font-bold tracking-[0.1em] uppercase hover:border-terracotta hover:text-terracotta transition-all"
                    >
                      Leave Another Review
                    </button>
                    <button
                      onClick={() => setShowReviewModal(false)}
                      className="bg-transparent text-terracotta px-6 py-3 font-body text-[12px] font-bold tracking-[0.1em] uppercase hover:underline transition-all"
                    >
                      Back to Ordering
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── WEEKLY SPECIAL / FEATURED ─────────────────── */}
        <section className="bg-umber">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
              <div className="flex-1">
                <span className="inline-block font-body text-[11px] font-semibold tracking-[0.3em] uppercase text-terracotta mb-3">Daily Special</span>
                <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95] mb-4" style={{ fontSize: 'clamp(32px, 4vw, 52px)' }}>
                  Full Mukokoroshi Platter
                </h2>
                <p className="text-sm text-ink-mid mb-2 leading-relaxed max-w-md">
                  Our signature dish — a generous serving of traditional Mukokoroshi with all the trimmings. A true taste of Limpopo, prepared fresh daily.
                </p>
                <p className="font-display text-3xl text-terracotta mb-6">R85.00</p>
                <button
                  onClick={() => {
                    const desres = BUSINESSES.find(b => b.key === 'desres')
                    if (desres) { setBusiness(desres); setBranch(desres.branches[0]); setCart([]); setStep('menu') }
                  }}
                  className="rounded-full bg-terracotta hover:bg-terracotta-glow text-white px-8 py-3.5 font-body text-[13px] font-bold tracking-[0.1em] uppercase transition-all hover:-translate-y-0.5 inline-flex items-center gap-2"
                >
                  <ArrowRight size={14} /> Order Now
                </button>
              </div>
              <div className="flex-1 grid grid-cols-2 gap-4">
                <div className="bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden">
                  <div className="h-32 bg-surface-high flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
                    <Beef size={36} className="text-muted" />
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-body font-semibold text-cream">Steak Platter</p>
                    <p className="text-xs text-muted mt-1">From R65</p>
                  </div>
                </div>
                <div className="bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden">
                  <div className="h-32 bg-surface-high flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
                    <Croissant size={36} className="text-muted" />
                  </div>
                  <div className="p-4">
                    <p className="text-sm font-body font-semibold text-cream">Fresh Scones</p>
                    <p className="text-xs text-muted mt-1">From R2</p>
                  </div>
                </div>
                <div className="col-span-2 rounded-2xl border border-ochre/30 bg-[#FFFBF0] p-5 text-center">
                  <Star size={20} className="text-ochre mx-auto mb-2" />
                  <p className="font-body text-[11px] font-semibold tracking-[0.2em] uppercase text-ochre mb-1">Member Exclusive</p>
                  <p className="text-xs text-ink-mid">Order 5 times and get a free scone pack on your next visit!</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

        {/* ─── LOCATION SELECTION ────────────────────────── */}
        <section id="location-select" className="bg-charcoal">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
            <div className="text-center mb-10">
              <span className="inline-block font-body text-[11px] font-semibold tracking-[0.3em] uppercase text-terracotta mb-3">Choose Location</span>
              <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95]" style={{ fontSize: 'clamp(32px, 4vw, 52px)' }}>
                Where Are You?
              </h2>
              <p className="text-sm text-muted mt-3">Select your nearest branch to start ordering.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {LOCATIONS.map(({ biz, branch, city }) => {
                const Icon = BIZ_ICONS[biz.key] || Utensils
                return (
                  <button
                    key={biz.key}
                    onClick={() => { setBusiness(biz); setBranch(branch); setCart([]); setStep('menu') }}
                    className="group relative flex flex-col items-center gap-4 p-10 bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] hover:border-terracotta/40 transition-colors overflow-hidden text-center"
                  >
                    <div className="w-16 h-16 rounded-xl bg-surface-high flex items-center justify-center">
                      <MapPin size={28} className="text-ink-mid group-hover:text-terracotta transition-colors" />
                    </div>
                    <div>
                      <h3 className="font-display text-cream tracking-wide group-hover:text-terracotta transition-colors" style={{ fontSize: 'clamp(28px, 4vw, 40px)' }}>{city}</h3>
                      <p className="text-sm text-muted mt-1">{biz.label}</p>
                    </div>
                  </button>
                )
              })}
            </div>
            <p className="text-center text-xs text-muted mt-8">
              Want to browse first? <Link to="/menu" className="text-terracotta hover:underline">View our menu</Link>
            </p>
          </div>
        </section>

        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />

        {/* ─── TESTIMONIALS ──────────────────────────────── */}
        <section className="bg-umber">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16 sm:py-20">
            <div className="text-center mb-12">
              <h2 className="font-display text-cream tracking-[0.04em] leading-[0.95] mb-3" style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 800 }}>
                What Our Customers Say
              </h2>
              <p className="text-sm italic" style={{ color: '#E8761A' }}>Love is the secret ingredient.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {TESTIMONIALS.map((t, i) => (
                <div key={i} className="bg-surface border border-border-dark rounded-2xl shadow-[0_2px_12px_rgba(0,0,0,0.07)] overflow-hidden" style={{ padding: '24px' }}>
                  <div className="flex gap-0.5 mb-4">
                    {[1,2,3,4,5].map(s => (
                      <Star key={s} size={16} className={s <= t.rating ? 'text-[#E8761A] fill-[#E8761A]' : 'text-border-darker'} />
                    ))}
                  </div>
                  <p className="text-ink-mid italic leading-relaxed mb-5" style={{ fontSize: '15px' }}>"{t.text}"</p>
                  <div className="flex items-center gap-3">
                    {t.imageUrl ? (
                      <img src={t.imageUrl} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#E8761A] flex items-center justify-center">
                        <span className="text-white text-xs font-body font-bold">{t.initials}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-sm font-body font-bold text-cream block">{t.name}</span>
                      <span className="text-xs text-muted">{t.label}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    )
  }

  // ─── STEP 3: MENU (Sixty60-style browse & basket) ─────────────────────────
  const isCatEnabled = (catKey) => {
    if (!orderingPermissions || !business) return true
    return orderingPermissions[`${business.key}__${catKey}`] !== false
  }

  if (step === 'menu') {
    // Wait for permissions to load before showing menu
    if (orderingPermissions === null) {
      return (
        <div className="text-center py-20">
          <div className="w-8 h-8 border-2 border-terracotta border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-muted text-sm">Loading menu...</p>
        </div>
      )
    }

    const categories = business.categories
    const enabledCategories = categories.filter(c => isCatEnabled(c.key))

    // If all categories are disabled for this business, show closed message
    if (enabledCategories.length === 0) {
      return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <BackBtn onClick={() => { setBusiness(null); setBranch(null); setStep('business') }} />
          <div className="text-center py-20 bg-surface border border-border-dark rounded-2xl">
            <div className="w-16 h-16 rounded-full bg-surface-high flex items-center justify-center mx-auto mb-4">
              <Clock size={28} className="text-muted" />
            </div>
            <h2 className="font-display text-cream tracking-wide mb-3" style={{ fontSize: 'clamp(24px, 3vw, 36px)' }}>
              Ordering is Currently Closed
            </h2>
            <p className="text-muted text-sm max-w-md mx-auto">
              {business.label} is not accepting orders right now. Please check back later.
            </p>
          </div>
        </div>
      )
    }

    const searchLower = menuSearch.toLowerCase().trim()

    // Build items list based on active category + search
    let displayItems = []
    if (activeCategory) {
      const cat = categories.find(c => c.key === activeCategory)
      if (cat && isCatEnabled(cat.key)) {
        displayItems = cat.items.map(i => ({ ...i, _catKey: cat.key }))
      }
    } else {
      // "All" — show only enabled categories (excluding external-link categories)
      displayItems = categories
        .filter(c => !c.externalLink && isCatEnabled(c.key))
        .flatMap(cat => cat.items.map(i => ({ ...i, _catKey: cat.key })))
    }

    // Apply search filter
    if (searchLower) {
      displayItems = displayItems.filter(i => i.name.toLowerCase().includes(searchLower))
    }

    // Helper: format price with superscript cents
    const formatPrice = (price) => {
      const whole = Math.floor(price)
      const cents = Math.round((price - whole) * 100)
      if (cents === 0) return <span className="font-display text-lg text-cream">R{whole}</span>
      return (
        <span className="font-display text-lg text-cream">
          R{whole}<sup className="text-[10px] align-super">.{String(cents).padStart(2, '0')}</sup>
        </span>
      )
    }

    // Editable price component (shows edit icon only for logged-in users)
    const EditablePrice = ({ item }) => {
      const price = getPrice(item)
      if (editingPrice === item.id) {
        return (
          <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
            <span className="font-display text-lg text-cream">R</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editPriceValue}
              onChange={e => setEditPriceValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handlePriceSave(item.id)}
              className="w-20 bg-surface border border-terracotta px-2 py-0.5 font-display text-lg text-cream outline-none"
              autoFocus
            />
            <button
              onClick={() => handlePriceSave(item.id)}
              className="w-7 h-7 bg-terracotta text-white flex items-center justify-center rounded-full hover:bg-terracotta-glow"
            >
              <Check size={14} />
            </button>
            <button
              onClick={() => { setEditingPrice(null); setEditPriceValue('') }}
              className="w-7 h-7 bg-surface border border-border-dark text-muted flex items-center justify-center rounded-full hover:text-cream"
            >
              <X size={14} />
            </button>
          </div>
        )
      }
      return (
        <span className="inline-flex items-center gap-1.5">
          {formatPrice(price)}
          {user && (
            <button
              onClick={e => { e.stopPropagation(); setEditingPrice(item.id); setEditPriceValue(String(price)) }}
              className="w-6 h-6 bg-surface-high border border-border-dark flex items-center justify-center rounded-full text-muted hover:text-terracotta hover:border-terracotta transition-colors"
              title="Edit price"
            >
              <Pencil size={11} />
            </button>
          )}
        </span>
      )
    }

    // ─── Sixty60-style Product Card ───
    const ProductCard = ({ item, idx }) => {
      const qty = getItemQty(item.id)
      const CatIcon = CATEGORY_ICONS[item._catKey] || UtensilsCrossed
      const available = isAvailable(item.id)

      // Hide unavailable items from customers (non-logged-in users)
      if (!available && !user) return null

      return (
        <div
          className={`relative bg-surface rounded-2xl border border-border-dark overflow-hidden flex-shrink-0 w-[160px] sm:w-[200px] lg:w-[220px] animate-[flyIn_0.4s_ease-out_both] ${available ? 'cursor-pointer' : 'opacity-50'}`}
          style={{ animationDelay: `${Math.min(idx, 12) * 40}ms` }}
          onClick={() => available && openDetail(item)}
        >
          {/* Image container */}
          <div className="relative h-[140px] sm:h-[160px] bg-surface-high flex items-center justify-center p-2">
            <ImageSlideshow
              images={productThumbs.get(item.id)}
              fallback={<CatIcon size={40} className="text-muted" />}
              className="w-full h-full"
              useThumbs
            />
            {/* Unavailable overlay */}
            {!available && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="bg-black/70 text-white text-xs font-body font-bold px-3 py-1 uppercase tracking-wider">Unavailable</span>
              </div>
            )}
            {/* Availability toggle (staff only) */}
            {user && (
              <button
                className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition-all z-10 ${available ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'}`}
                onClick={e => { e.stopPropagation(); toggleAvailability(item.id) }}
                title={available ? 'Mark as unavailable' : 'Mark as available'}
              >
                {available ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            )}
            {/* Circular add / stepper overlay */}
            {available && (
              <div className="absolute top-2 right-2" onClick={e => e.stopPropagation()}>
                {qty === 0 ? (
                  <button
                    onClick={() => addToCart(item)}
                    className="w-9 h-9 rounded-full bg-terracotta hover:bg-terracotta-glow text-white flex items-center justify-center transition-all"
                    aria-label={`Add ${item.name}`}
                  >
                    <Plus size={18} />
                  </button>
                ) : (
                  <div className="flex items-center gap-1 bg-surface rounded-full border border-border-dark px-1 py-0.5">
                    <button
                      onClick={() => updateQty(item.id, -1)}
                      className="w-7 h-7 rounded-full bg-surface-high flex items-center justify-center text-cream hover:border-terracotta transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-xs font-body font-bold text-cream w-5 text-center">{qty}</span>
                    <button
                      onClick={() => updateQty(item.id, 1)}
                      className="w-7 h-7 rounded-full bg-terracotta text-white flex items-center justify-center hover:bg-terracotta-glow transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Card body */}
          <div className="p-3">
            {/* Price */}
            <EditablePrice item={item} />
            {/* Name */}
            <p className="font-body text-sm text-ink-mid mt-1 line-clamp-2 leading-tight min-h-[2.25rem]">{item.name}</p>
            {/* Branch pill */}
            <div className="mt-2">
              <span className="bg-surface-high text-muted text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <MapPin size={10} /> {branch.label}
              </span>
            </div>
          </div>
        </div>
      )
    }

    // ─── Grid Product Card (for search results / View All) ───
    const GridProductCard = ({ item, idx }) => {
      const qty = getItemQty(item.id)
      const CatIcon = CATEGORY_ICONS[item._catKey] || UtensilsCrossed
      const available = isAvailable(item.id)

      if (!available && !user) return null

      return (
        <div
          className={`relative bg-surface rounded-2xl border border-border-dark overflow-hidden animate-[flyIn_0.4s_ease-out_both] ${available ? 'cursor-pointer' : 'opacity-50'}`}
          style={{ animationDelay: `${Math.min(idx, 12) * 40}ms` }}
          onClick={() => available && openDetail(item)}
        >
          {/* Image container */}
          <div className="relative h-[140px] sm:h-[160px] bg-surface-high flex items-center justify-center p-2">
            <ImageSlideshow
              images={productThumbs.get(item.id)}
              fallback={<CatIcon size={40} className="text-muted" />}
              className="w-full h-full"
              useThumbs
            />
            {!available && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <span className="bg-black/70 text-white text-xs font-body font-bold px-3 py-1 uppercase tracking-wider">Unavailable</span>
              </div>
            )}
            {user && (
              <button
                className={`absolute top-2 left-2 w-8 h-8 rounded-full flex items-center justify-center transition-all z-10 ${available ? 'bg-green-500/90 text-white' : 'bg-red-500/90 text-white'}`}
                onClick={e => { e.stopPropagation(); toggleAvailability(item.id) }}
                title={available ? 'Mark as unavailable' : 'Mark as available'}
              >
                {available ? <Eye size={14} /> : <EyeOff size={14} />}
              </button>
            )}
            {available && (
              <div className="absolute top-2 right-2" onClick={e => e.stopPropagation()}>
                {qty === 0 ? (
                  <button
                    onClick={() => addToCart(item)}
                    className="w-9 h-9 rounded-full bg-terracotta hover:bg-terracotta-glow text-white flex items-center justify-center transition-all"
                    aria-label={`Add ${item.name}`}
                  >
                    <Plus size={18} />
                  </button>
                ) : (
                  <div className="flex items-center gap-1 bg-surface rounded-full border border-border-dark px-1 py-0.5">
                    <button
                      onClick={() => updateQty(item.id, -1)}
                      className="w-7 h-7 rounded-full bg-surface-high flex items-center justify-center text-cream hover:border-terracotta transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus size={12} />
                    </button>
                    <span className="text-xs font-body font-bold text-cream w-5 text-center">{qty}</span>
                    <button
                      onClick={() => updateQty(item.id, 1)}
                      className="w-7 h-7 rounded-full bg-terracotta text-white flex items-center justify-center hover:bg-terracotta-glow transition-colors"
                    aria-label="Increase quantity"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              )}
            </div>
            )}
          </div>
          <div className="p-3">
            <EditablePrice item={item} />
            <p className="font-body text-sm text-ink-mid mt-1 line-clamp-2 leading-tight min-h-[2.25rem]">{item.name}</p>
            <div className="mt-2">
              <span className="bg-surface-high text-muted text-[10px] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                <MapPin size={10} /> {branch.label}
              </span>
            </div>
          </div>
        </div>
      )
    }

    // Determine if we should show category sections (no search, no viewAllCat)
    const showCategorySections = !searchLower && !viewAllCat

    // Build items for grid view (search or viewAllCat)
    let gridItems = []
    if (viewAllCat && !searchLower) {
      const cat = categories.find(c => c.key === viewAllCat)
      if (cat && isCatEnabled(cat.key)) {
        gridItems = cat.items.map(i => ({ ...i, _catKey: cat.key }))
      }
    } else if (searchLower) {
      gridItems = displayItems
    }

    return (
      <div className="pb-28">
        {/* ─── Sticky Top Bar ─── */}
        <div className="sticky top-0 z-40 bg-umber border-b border-border-dark">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            {/* Top row: back + branch name + cart */}
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center gap-2 min-w-0">
                <button
                  onClick={() => {
                    if (viewAllCat) { setViewAllCat(null) }
                    else { setStep('business'); setBusiness(null); setBranch(null); setCart([]) }
                  }}
                  className="text-muted hover:text-terracotta transition-colors shrink-0"
                >
                  <ArrowLeft size={20} />
                </button>
                <div className="min-w-0">
                  <h1 className="font-display text-cream tracking-[0.04em] leading-none text-lg sm:text-xl truncate">
                    {viewAllCat
                      ? categories.find(c => c.key === viewAllCat)?.label || 'Products'
                      : business.label}
                  </h1>
                  {!viewAllCat && <p className="text-[11px] text-muted truncate">{branch.label} Branch</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {cartCount > 0 && (
                  <button
                    onClick={() => setStep('cart')}
                    className="relative flex items-center gap-2 bg-terracotta hover:bg-terracotta-glow text-white px-3 py-2 rounded-full font-body text-[12px] font-bold tracking-[0.08em] uppercase transition-all"
                  >
                    <ShoppingCart size={16} />
                    <span className="hidden sm:inline">Cart</span>
                    <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white text-terracotta text-[10px] font-bold flex items-center justify-center">{cartCount}</span>
                  </button>
                )}
              </div>
            </div>

            {/* Search input */}
            <div className="relative pb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" style={{ top: 'calc(50% - 6px)' }} />
              <input
                type="text"
                value={menuSearch}
                onChange={e => { setMenuSearch(e.target.value); if (e.target.value) setViewAllCat(null) }}
                placeholder="Search products..."
                className="w-full bg-surface-high border border-border-dark rounded-xl pl-10 pr-10 py-2.5 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta"
              />
              {menuSearch && (
                <button onClick={() => setMenuSearch('')} className="absolute right-3 text-muted hover:text-cream" style={{ top: 'calc(50% - 6px)' }}>
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ─── Main content area ─── */}
        <section className="bg-umber">
          {showCategorySections ? (
            /* ─── Category Sections with Horizontal Scroll ─── */
            <div className="py-4 space-y-6">
              {/* External link categories as chips */}
              {categories.filter(c => c.externalLink && isCatEnabled(c.key)).length > 0 && (
                <div className="px-4">
                  <div className="flex gap-2 flex-wrap">
                    {categories.filter(c => c.externalLink && isCatEnabled(c.key)).map(cat => (
                      <a
                        key={cat.key}
                        href={cat.externalLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 px-4 py-2.5 font-body text-[12px] font-semibold tracking-[0.06em] uppercase border transition-all whitespace-nowrap inline-flex items-center gap-1.5 bg-surface text-cream border-border-dark hover:border-terracotta/40 rounded-xl"
                      >
                        {cat.label} <ExternalLink size={10} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {categories
                .filter(c => !c.externalLink && isCatEnabled(c.key) && c.items.length > 0)
                .map(cat => (
                  <div key={cat.key}>
                    {/* Section heading row */}
                    <div className="flex items-center justify-between px-4 mb-3">
                      <h2 className="font-display text-cream text-xl sm:text-2xl tracking-[0.04em] uppercase">{cat.label}</h2>
                      <button
                        onClick={() => { setViewAllCat(cat.key); setActiveCategory(cat.key) }}
                        className="text-terracotta font-body text-sm font-semibold hover:text-terracotta-glow transition-colors whitespace-nowrap"
                      >
                        View All
                      </button>
                    </div>
                    {/* Horizontally scrollable row of cards */}
                    <div
                      className="flex gap-3 overflow-x-auto px-4 scrollbar-hide"
                      style={{ WebkitOverflowScrolling: 'touch' }}
                    >
                      {cat.items.map((item, idx) => (
                        <ProductCard key={item.id} item={{ ...item, _catKey: cat.key }} idx={idx} />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            /* ─── Grid view (search results or View All) ─── */
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6">
              {searchLower && (
                <p className="text-sm text-muted mb-4">{gridItems.length} result{gridItems.length !== 1 ? 's' : ''} for &ldquo;{menuSearch}&rdquo;</p>
              )}
              {viewAllCat && !searchLower && (
                <button
                  onClick={() => setViewAllCat(null)}
                  className="text-xs text-muted hover:text-terracotta font-body tracking-[0.05em] uppercase inline-flex items-center gap-1 mb-4"
                >
                  <ArrowLeft size={12} /> Back to categories
                </button>
              )}

              {gridItems.length === 0 ? (
                <div className="text-center py-16">
                  <Search size={40} className="text-muted mx-auto mb-3" />
                  <p className="text-muted text-sm">No products found.</p>
                  {searchLower && (
                    <button onClick={() => setMenuSearch('')} className="mt-2 text-terracotta hover:underline text-sm font-body">Clear search</button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {gridItems.map((item, idx) => (
                    <GridProductCard key={item.id} item={item} idx={idx} />
                  ))}
                </div>
              )}
            </div>
          )}
        </section>

        {/* ─── Floating basket bar ─── */}
        {cartCount > 0 && (
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-[rgba(240,235,225,0.97)] backdrop-blur-xl border-t border-border-dark" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <ShoppingCart size={22} className="text-cream" />
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-terracotta text-white text-[10px] font-bold flex items-center justify-center">{cartCount}</span>
                </div>
                <div>
                  <span className="font-display text-xl text-cream">R{cartTotal.toFixed(2)}</span>
                  <span className="text-[10px] text-muted block leading-none">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <button
                onClick={() => setStep('cart')}
                className="bg-terracotta hover:bg-terracotta-glow text-white px-6 py-3 font-body text-[13px] font-bold tracking-[0.1em] uppercase transition-all flex items-center gap-2"
              >
                View Basket <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {/* ─── Product detail modal ─── */}
        {detailItem && (
          <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={closeDetail}>
            <div className="absolute inset-0 bg-black/50" />
            <div
              className="relative w-full sm:max-w-md bg-umber border-t sm:border border-border-dark overflow-hidden animate-[flyIn_0.3s_ease-out_both]"
              style={{ maxHeight: '90vh', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Close */}
              <button onClick={closeDetail} className="absolute top-4 right-4 z-10 w-9 h-9 bg-surface border border-border-dark flex items-center justify-center text-muted hover:text-cream">
                <X size={18} />
              </button>

              {/* Product image or icon fallback */}
              <ImageSlideshow
                images={detailFullImages || productThumbs.get(detailItem.id)}
                fallback={(() => { const CatIcon = CATEGORY_ICONS[detailItem._catKey] || UtensilsCrossed; return <CatIcon size={48} className="text-muted" /> })()}
                className="w-full h-48"
              />

              {/* Content */}
              <div className="p-5 space-y-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 12rem)' }}>
                <div>
                  <h3 className="font-display text-cream tracking-wide text-2xl">{detailItem.name}</h3>
                  <p className="font-display text-terracotta text-xl mt-1">R{getPrice(detailItem).toFixed(2)}</p>
                </div>

                {/* Quantity selector */}
                <div>
                  <label className="text-xs text-muted font-body uppercase tracking-wider block mb-2">Quantity</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setDetailQty(q => Math.max(1, q - 1))}
                      className="w-11 h-11 bg-surface border border-border-dark flex items-center justify-center text-cream hover:border-terracotta transition-colors"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-display text-2xl text-cream w-10 text-center">{detailQty}</span>
                    <button
                      onClick={() => setDetailQty(q => q + 1)}
                      className="w-11 h-11 bg-terracotta/10 border border-terracotta/30 flex items-center justify-center text-terracotta hover:bg-terracotta/20 transition-colors"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>

                {/* Special request */}
                <div>
                  <label className="text-xs text-muted font-body uppercase tracking-wider block mb-2">
                    Special Request <span className="normal-case tracking-normal">(optional)</span>
                  </label>
                  <textarea
                    value={detailNote}
                    onChange={e => setDetailNote(e.target.value)}
                    placeholder="e.g. Extra spicy, no onions..."
                    rows={2}
                    maxLength={200}
                    className="w-full bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta resize-none"
                  />
                  <p className="text-[10px] text-muted text-right mt-1">{detailNote.length}/200</p>
                </div>

                {/* Add / Update button */}
                <button
                  onClick={confirmDetail}
                  className="w-full bg-terracotta hover:bg-terracotta-glow text-white py-4 font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all"
                >
                  {getItemQty(detailItem.id) > 0 ? (
                    <>Update Basket — R{(getPrice(detailItem) * detailQty).toFixed(2)}</>
                  ) : (
                    <><Plus size={16} /> Add to Basket — R{(getPrice(detailItem) * detailQty).toFixed(2)}</>
                  )}
                </button>

                {/* Remove if already in cart */}
                {getItemQty(detailItem.id) > 0 && (
                  <button
                    onClick={() => { removeFromCart(detailItem.id); closeDetail() }}
                    className="w-full text-center text-sm text-muted hover:text-terracotta font-body py-2 flex items-center justify-center gap-1"
                  >
                    <Trash2 size={14} /> Remove from basket
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  // ─── STEP 4: CART ────────────────────────────────────
  if (step === 'cart') {
    return (
      <div>
        <Header title="Your Basket" subtitle={`${business.label} — ${branch.label}`} />
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <section className="bg-umber">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
            <BackBtn onClick={() => setStep('menu')} label="Continue Shopping" />

            {cart.length === 0 ? (
              <div className="text-center py-16">
                <ShoppingCart size={48} className="text-muted mx-auto mb-4" />
                <p className="text-muted text-sm">Your basket is empty.</p>
                <button onClick={() => setStep('menu')} className="mt-4 text-terracotta hover:underline text-sm font-body">Browse menu</button>
              </div>
            ) : (
              <>
                <div className="bg-surface border border-border-dark divide-y divide-border-dark">
                  {cart.map(item => (
                    <div key={item.id} className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-body font-semibold text-cream truncate">{item.name}</h4>
                          <p className="text-xs text-muted">R{item.price.toFixed(2)} each</p>
                          {item.note && (
                            <p className="text-[11px] text-muted mt-1 flex items-center gap-1">
                              <MessageSquare size={10} className="text-terracotta shrink-0" />
                              <span className="truncate">{item.note}</span>
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button onClick={() => updateQty(item.id, -1)} className="w-9 h-9 bg-surface-high border border-border-dark flex items-center justify-center text-cream hover:border-terracotta transition-colors"><Minus size={14} /></button>
                          <span className="text-sm font-body font-bold text-cream w-6 text-center">{item.quantity}</span>
                          <button onClick={() => updateQty(item.id, 1)} className="w-9 h-9 bg-terracotta/10 border border-terracotta/30 flex items-center justify-center text-terracotta hover:bg-terracotta/20 transition-colors"><Plus size={14} /></button>
                        </div>
                        <span className="font-display text-lg text-terracotta w-20 text-right">R{(item.price * item.quantity).toFixed(2)}</span>
                        <button onClick={() => removeFromCart(item.id)} className="text-muted hover:text-terracotta transition-colors"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center mt-6 px-2">
                  <span className="text-sm font-body font-bold text-cream">Order Total</span>
                  <span className="font-display text-2xl text-terracotta">R{cartTotal.toFixed(2)}</span>
                </div>

                <button
                  onClick={() => setStep('checkout')}
                  className="w-full mt-6 bg-terracotta hover:bg-terracotta-glow text-white py-4 font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
                >
                  Proceed to Checkout <ArrowRight size={16} />
                </button>
              </>
            )}
          </div>
        </section>
      </div>
    )
  }

  // ─── STEP 5: CHECKOUT ──────────────────────────────────
  if (step === 'checkout') {
    const handleCheckout = (e) => {
      e.preventDefault()
      setError('')
      if (!form.name.trim() || !form.whatsapp.trim() || !form.collectionDate) {
        setError('Please fill in all fields.')
        return
      }
      setStep('payment')
    }

    return (
      <div>
        <Header title="Checkout" subtitle={`${business.label} — ${branch.label}`} />
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <section className="bg-umber">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
            <BackBtn onClick={() => setStep('cart')} label="Back to Basket" />

            <form onSubmit={handleCheckout} className="space-y-6">
              {/* Details */}
              <div className="relative bg-surface border border-border-dark overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-terracotta" />
                <div className="p-6 pl-8 space-y-4">
                  <h3 className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta flex items-center gap-2">
                    <User size={14} /> Your Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs text-muted mb-1 block font-body">Full Name *</label>
                      <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Thabo Mokoena" className="w-full bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta" required />
                    </div>
                    <div>
                      <label className="text-xs text-muted mb-1 block font-body flex items-center gap-1"><Phone size={10} /> WhatsApp *</label>
                      <input type="tel" value={form.whatsapp} onChange={e => setForm(p => ({ ...p, whatsapp: e.target.value }))} placeholder="e.g. 0812345678" className="w-full bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta" required />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-muted mb-1 block font-body flex items-center gap-1"><Mail size={10} /> Email (optional)</label>
                    <input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="e.g. you@example.com" className="w-full bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream placeholder:text-muted/50 focus:outline-none focus:border-terracotta" />
                  </div>
                </div>
              </div>

              {/* Collection date */}
              <div className="relative bg-surface border border-border-dark overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-terracotta" />
                <div className="p-6 pl-8 space-y-4">
                  <h3 className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta flex items-center gap-2">
                    <Calendar size={14} /> Collection Date
                  </h3>
                  <p className="text-xs text-muted">Orders require at least 12 hours advance notice.</p>
                  <input type="date" min={getMinDate()} value={form.collectionDate} onChange={e => setForm(p => ({ ...p, collectionDate: e.target.value }))} className="w-full sm:w-auto bg-surface-high border border-border-dark px-4 py-3 font-body text-sm text-cream focus:outline-none focus:border-terracotta" required />
                </div>
              </div>

              {/* Order summary */}
              <div className="relative bg-surface border border-border-dark overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-terracotta" />
                <div className="p-6 pl-8 space-y-3">
                  <h3 className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta flex items-center gap-2">
                    <ShoppingCart size={14} /> Order Summary
                  </h3>
                  {cart.map(item => (
                    <div key={item.id} className="flex justify-between text-sm">
                      <div className="flex-1 min-w-0">
                        <span className="text-cream">{item.name} x{item.quantity}</span>
                        {item.note && <p className="text-[10px] text-muted truncate">{item.note}</p>}
                      </div>
                      <span className="text-cream font-body font-medium ml-3">R{(item.price * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-border-dark pt-3 flex justify-between">
                    <span className="text-sm font-body font-bold text-cream">Total</span>
                    <span className="font-display text-2xl text-terracotta">R{cartTotal.toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {error && <p className="text-sm text-terracotta bg-ember-bg border border-terracotta/30 px-4 py-3">{error}</p>}

              <button type="submit" className="w-full bg-terracotta hover:bg-terracotta-glow text-white py-4 font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5">
                Continue to Payment <ArrowRight size={16} />
              </button>
            </form>
          </div>
        </section>
      </div>
    )
  }

  // ─── STEP 6: PAYMENT ─────────────────────────────────
  if (step === 'payment') {
    return (
      <div>
        <Header title="Payment" subtitle={`Total: R${cartTotal.toFixed(2)}`} />
        <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, #E8761A, transparent)' }} />
        <section className="bg-umber">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-12">
            <BackBtn onClick={() => { setStep('checkout'); setPaymentMethod(null); setError('') }} />

            {/* Order summary compact */}
            <div className="relative bg-surface border border-border-dark overflow-hidden mb-6">
              <div className="absolute top-0 left-0 w-1 h-full bg-terracotta" />
              <div className="p-6 pl-8">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted">Customer</span>
                  <span className="text-cream font-body font-medium">{form.name} ({form.whatsapp})</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted">Collection</span>
                  <span className="text-cream font-body font-medium">{formatDate(form.collectionDate)} — {branch.label}</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted">Items</span>
                  <span className="text-cream font-body font-medium">{cartCount} item{cartCount !== 1 ? 's' : ''}</span>
                </div>
                <div className="border-t border-border-dark pt-2 mt-2 flex justify-between">
                  <span className="text-sm font-body font-bold text-cream">Total</span>
                  <span className="font-display text-2xl text-terracotta">R{cartTotal.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Payment method selection */}
            <div className="relative bg-surface border border-border-dark overflow-hidden mb-6">
              <div className="absolute top-0 left-0 w-1 h-full bg-terracotta" />
              <div className="p-6 pl-8 space-y-4">
                <h3 className="font-body text-[11px] font-semibold tracking-[0.25em] uppercase text-terracotta flex items-center gap-2">
                  <CreditCard size={14} /> Choose Payment Method
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <button
                    onClick={() => business.cardEnabled && setPaymentMethod('card')}
                    disabled={!business.cardEnabled}
                    className={`flex flex-col items-center gap-3 p-6 border transition-all text-center ${
                      !business.cardEnabled
                        ? 'border-border-dark opacity-50 cursor-not-allowed'
                        : paymentMethod === 'card' ? 'border-terracotta bg-ember-bg' : 'border-border-dark hover:border-terracotta/40'
                    }`}
                  >
                    <CreditCard size={28} className={paymentMethod === 'card' ? 'text-terracotta' : 'text-muted'} />
                    <span className={`text-sm font-body font-semibold ${paymentMethod === 'card' ? 'text-terracotta' : 'text-cream'}`}>Pay with Card</span>
                    <span className="text-xs text-muted">{business.cardEnabled ? 'Powered by Yoco' : 'Coming soon'}</span>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`flex flex-col items-center gap-3 p-6 border transition-all text-center ${
                      paymentMethod === 'cash' ? 'border-terracotta bg-ember-bg' : 'border-border-dark hover:border-terracotta/40'
                    }`}
                  >
                    <Banknote size={28} className={paymentMethod === 'cash' ? 'text-terracotta' : 'text-muted'} />
                    <span className={`text-sm font-body font-semibold ${paymentMethod === 'cash' ? 'text-terracotta' : 'text-cream'}`}>Cash on Collection</span>
                    <span className="text-xs text-muted">Pay when you collect</span>
                  </button>
                </div>
              </div>
            </div>

            {error && <p className="text-sm text-terracotta bg-ember-bg border border-terracotta/30 px-4 py-3 mb-6">{error}</p>}

            <button
              onClick={paymentMethod === 'cash' ? handleCashOrder : handleCardPayment}
              disabled={!paymentMethod || submitting}
              className="w-full bg-terracotta hover:bg-terracotta-glow disabled:opacity-50 text-white py-4 font-body text-[13px] font-bold tracking-[0.1em] uppercase flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5"
            >
              {submitting ? (
                <><Loader size={16} className="animate-spin" /> Processing...</>
              ) : paymentMethod === 'cash' ? (
                <><Banknote size={16} /> Confirm Order — Pay on Collection</>
              ) : paymentMethod === 'card' ? (
                <><CreditCard size={16} /> Pay R{cartTotal.toFixed(2)} Now</>
              ) : (
                'Select a payment method'
              )}
            </button>
          </div>
        </section>
      </div>
    )
  }

  // Success step now handled by dedicated /order/success page

  return null
}
